import React, { useState, useEffect, useContext, useCallback } from 'react';
import { firestore } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import './TimeSlots.css';

const TimeSlots = ({ practitionerId, selectedDate, serviceId, serviceDuration, onTimeSelect }) => {
  const { setCurrentStep, updateBookingData, bookingData } = useContext(BookingContext);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRemoteOnlyDay, setIsRemoteOnlyDay] = useState(false);

  // Récupérer les valeurs actuelles du contexte
  const isRemoteAppointment = bookingData.isRemote || false;

    // Récupérer les créneaux horaires disponibles
  const fetchAvailableTimeSlots = useCallback(async () => {
    if (!selectedDate || !practitionerId || !serviceId) return;

    setLoading(true);
    setError(null);
    setTimeSlots([]);
    setSelectedTimeSlot(null);

    try {
      // Formater la date sélectionnée au format YYYY-MM-DD en tenant compte du fuseau horaire
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      console.log("Date formatée pour requête:", dateStr);
      console.log("Date d'origine:", selectedDate);

      // Récupérer le service pour connaître sa durée, le temps de repos et la limite quotidienne
      const serviceDoc = await firestore.collection('services').doc(serviceId).get();

      if (!serviceDoc.exists) {
        setError("Le service sélectionné n'existe pas.");
        setLoading(false);
        return;
      }

      const service = serviceDoc.data();
      const duration = serviceDuration || service.duration || 60; // Durée en minutes
      const restTime = service.restTime || 0; // Temps de repos en minutes
      const dailyLimit = service.dailyLimit || 0; // Limite quotidienne, 0 = pas de limite

      console.log("Service récupéré:", service);
      console.log("Durée du service:", duration, "minutes");
      console.log("Temps de repos:", restTime, "minutes");
      console.log("Limite quotidienne:", dailyLimit, "rendez-vous par jour");

      // Utiliser la nouvelle fonction pour récupérer les disponibilités
      const availability = await getAvailabilityForDate(practitionerId, dateStr);

      if (!availability) {
        setError("Aucune disponibilité définie pour cette date.");
        setLoading(false);

        // Signaler au calendrier que ce jour n'a pas de créneaux disponibles
        if (window.updateDaysWithoutSlots) {
          window.updateDaysWithoutSlots(dateStr, true);
        }

        return;
      }

      // Vérifier si c'est un jour travaillé
      if (!availability.isWorkingDay) {
        setError("Le praticien ne travaille pas ce jour-là.");
        setLoading(false);

        // Signaler au calendrier que ce jour n'a pas de créneaux disponibles
        if (window.updateDaysWithoutSlots) {
          window.updateDaysWithoutSlots(dateStr, true);
        }

        return;
      }

      // Vérifier les contraintes liées aux rendez-vous à distance
      if (availability.remoteOnly) {
        console.log("Ce jour est uniquement pour les rendez-vous à distance");
        setIsRemoteOnlyDay(true);
      } else {
        setIsRemoteOnlyDay(false);
      }

      // Vérifier si ce jour n'autorise pas les rendez-vous à distance et que le service est à distance uniquement
      if (!availability.allowsRemote && bookingData.remoteOnly) {
        setError("Ce jour n'est pas disponible pour les consultations à distance.");
        setLoading(false);

        // Signaler au calendrier que ce jour n'a pas de créneaux disponibles
        if (window.updateDaysWithoutSlots) {
          window.updateDaysWithoutSlots(dateStr, true);
        }

        return;
      }

      // Vérifier si ce jour est uniquement à distance et que le service ne l'autorise pas
      if (availability.remoteOnly && !bookingData.canBeRemote) {
        setError("Ce jour est uniquement pour les consultations à distance, mais le service sélectionné ne peut être dispensé à distance.");
        setLoading(false);

        // Signaler au calendrier que ce jour n'a pas de créneaux disponibles
        if (window.updateDaysWithoutSlots) {
          window.updateDaysWithoutSlots(dateStr, true);
        }

        return;
      }

      // Récupérer les rendez-vous existants pour cette date
      const appointmentsSnapshot = await firestore
        .collection('appointments')
        .where('practitionerId', '==', practitionerId)
        .where('date', '==', dateStr)
        .where('status', '==', 'confirmed')
        .get();

      const bookedSlots = appointmentsSnapshot.docs.map(doc => {
        const data = doc.data();
        // Ajouter à la durée le temps de repos associé au service du rendez-vous
        const appointmentDuration = data.duration || 60;
        const appointmentRestTime = data.restTime || 0;

        return {
          start: convertTimeToMinutes(data.time),
          end: convertTimeToMinutes(data.time) + appointmentDuration + appointmentRestTime,
          serviceId: data.serviceId,
          isRemote: data.isRemote || false
        };
      });

      console.log("Rendez-vous existants (avec temps de repos):", bookedSlots);

      // Vérifier si la limite quotidienne pour ce service est atteinte
      if (dailyLimit > 0) {
        // Compter le nombre de rendez-vous pour ce service spécifique à cette date
        const serviceAppointmentsCount = bookedSlots.filter(slot => slot.serviceId === serviceId).length;
        console.log(`Nombre de rendez-vous existants pour le service ${serviceId}: ${serviceAppointmentsCount}/${dailyLimit}`);

        if (serviceAppointmentsCount >= dailyLimit) {
          setError("La limite de rendez-vous quotidienne pour ce service est atteinte.");
          // Signaler au calendrier que ce jour n'a pas de créneaux disponibles pour ce service
          if (window.updateDaysWithoutSlots) {
            window.updateDaysWithoutSlots(dateStr, true);
          }

          setLoading(false);
          return;
        }
      }

      // Récupérer les absences partielles pour cette date
      const vacationsSnapshot = await firestore
        .collection('vacations')
        .where('practitionerId', '==', practitionerId)
        .where('isFullDay', '==', false) // Uniquement les absences partielles
        .get();

      const partialAbsences = [];

      // Traiter les absences qui incluent cette date
      vacationsSnapshot.docs.forEach(doc => {
        const vacation = doc.data();
        const startDate = new Date(vacation.startDate);
        const endDate = new Date(vacation.endDate);
        const selectedDateObj = new Date(dateStr);

        // Convertir en dates sans heures pour la comparaison
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        selectedDateObj.setHours(0, 0, 0, 0);

        // Vérifier si la date sélectionnée tombe dans la période d'absence
        if (selectedDateObj >= startDate && selectedDateObj <= endDate) {
          partialAbsences.push({
            start: convertTimeToMinutes(vacation.startTime),
            end: convertTimeToMinutes(vacation.endTime)
          });
        }
      });

      console.log("Absences partielles pour cette date:", partialAbsences);

      // Calcul de la durée totale en incluant le temps de repos
      // pour vérifier la disponibilité
      const totalDuration = parseInt(duration) + parseInt(restTime);
      console.log("Durée totale (service + repos):", totalDuration, "minutes");

      const slots = generateTimeSlots(
        availability.startTime,
        availability.endTime,
        availability.hasLunchBreak ? availability.lunchBreak?.start : null,
        availability.hasLunchBreak ? availability.lunchBreak?.end : null,
        duration,
        totalDuration,
        bookedSlots,
        partialAbsences,
        isRemoteAppointment // Utiliser l'option choisie à l'étape 3
      );

      console.log("Créneaux générés:", slots.length);

      // Mettre à jour l'état avec les créneaux générés
      setTimeSlots(slots);

      // Si aucun créneau disponible, afficher une erreur
      if (slots.length === 0) {
        setError("Aucun créneau disponible pour cette date.");

        // Important: Mise à jour du contexte global pour marquer cette date comme sans créneaux
        // C'est ici que nous informons le composant Calendar qu'il n'y a pas de créneaux disponibles
        if (window.updateDaysWithoutSlots) {
          window.updateDaysWithoutSlots(dateStr, true);
        }
      } else {
        // Si des créneaux sont disponibles, mettre à jour le contexte global
        if (window.updateDaysWithoutSlots) {
          window.updateDaysWithoutSlots(dateStr, false);
        }
      }

    } catch (error) {
      console.error("Erreur lors du chargement des créneaux horaires:", error);
      setError("Une erreur est survenue lors du chargement des créneaux horaires.");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, practitionerId, serviceId, serviceDuration, isRemoteAppointment, bookingData.remoteOnly, bookingData.canBeRemote]);

  
  // Charger les créneaux disponibles lorsque la date change
  useEffect(() => {
    if (selectedDate && practitionerId && serviceId) {
      console.log("Récupération des créneaux pour:", {
        date: selectedDate,
        dateFormatted: `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`,
        practitionerId,
        serviceId,
        isRemote: isRemoteAppointment
      });
      fetchAvailableTimeSlots();
    } else {
      setTimeSlots([]);
      setSelectedTimeSlot(null);
    }
  }, [selectedDate, practitionerId, serviceId, serviceDuration, isRemoteAppointment, fetchAvailableTimeSlots]);


  // Fonction améliorée pour récupérer les disponibilités
  const getAvailabilityForDate = async (practitionerId, dateStr) => {
    try {
      // D'abord, chercher une disponibilité spécifique pour cette date
      const specificSnapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', practitionerId)
        .where('type', '==', 'specific')
        .where('date', '==', dateStr)
        .limit(1)
        .get();

      if (!specificSnapshot.empty) {
        const specificAvailability = specificSnapshot.docs[0].data();
        console.log("Disponibilité spécifique trouvée pour", dateStr, ":", specificAvailability);
        return specificAvailability;
      }

      // Sinon, utiliser la disponibilité par défaut du jour de la semaine
      const selectedDateObj = new Date(dateStr);
      const dayOfWeek = selectedDateObj.getDay(); // 0 = dimanche, 1 = lundi, etc.
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // 0 = lundi, 6 = dimanche

      const defaultSnapshot = await firestore
        .collection('availability')
        .where('practitionerId', '==', practitionerId)
        .where('type', '==', 'default')
        .where('day', '==', adjustedDayOfWeek)
        .limit(1)
        .get();

      if (!defaultSnapshot.empty) {
        const defaultAvailability = defaultSnapshot.docs[0].data();
        console.log("Disponibilité par défaut trouvée pour le jour", adjustedDayOfWeek, ":", defaultAvailability);
        return defaultAvailability;
      }

      // Aucune disponibilité trouvée
      console.log("Aucune disponibilité trouvée pour", dateStr);
      return null;

    } catch (error) {
      console.error("Erreur lors de la récupération des disponibilités:", error);
      throw error;
    }
  };

  // Générer les créneaux horaires en fonction des disponibilités
  const generateTimeSlots = (startTime, endTime, lunchStart, lunchEnd, duration, totalDuration, bookedSlots, partialAbsences = [], isRemote = false) => {
    const slots = [];
    const interval = 15; // Intervalle entre les créneaux en minutes

    // Convertir les heures en minutes
    const startMinutes = convertTimeToMinutes(startTime);
    const endMinutes = convertTimeToMinutes(endTime);
    const lunchStartMinutes = lunchStart ? convertTimeToMinutes(lunchStart) : null;
    const lunchEndMinutes = lunchEnd ? convertTimeToMinutes(lunchEnd) : null;

    // Heure actuelle
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Si c'est aujourd'hui, ne pas proposer de créneaux déjà passés
    const isToday = selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    const currentMinutes = isToday
      ? now.getHours() * 60 + now.getMinutes() + 60 // Ajouter 1 heure de marge
      : 0;

    console.log("Génération de créneaux entre:", startTime, "et", endTime);
    console.log("Pause déjeuner:", lunchStart, "-", lunchEnd);
    console.log("Durée du service:", duration, "minutes");
    console.log("Durée totale (avec repos):", totalDuration, "minutes");
    console.log("Absences partielles:", partialAbsences);
    console.log("Type de rendez-vous:", isRemote ? "À distance" : "Présentiel");

    // Générer tous les créneaux possibles
    for (let minutes = startMinutes; minutes <= endMinutes - duration; minutes += interval) {
      // Vérifier si le créneau est déjà passé (si c'est aujourd'hui)
      if (isToday && minutes < currentMinutes) {
        continue;
      }

      // Vérifier si le créneau chevauche la pause déjeuner (seulement si hasLunchBreak est true)
      // Nous utilisons la durée de service (sans repos) pour vérifier si le service lui-même
      // chevauche la pause déjeuner
      const slotEndMinutes = minutes + duration;
      const isDuringLunch = lunchStartMinutes !== null &&
        ((minutes >= lunchStartMinutes && minutes < lunchEndMinutes) ||
          (slotEndMinutes > lunchStartMinutes && slotEndMinutes <= lunchEndMinutes) ||
          (minutes < lunchStartMinutes && slotEndMinutes > lunchEndMinutes));

      // Vérifier si le créneau chevauche une absence partielle
      const isDuringAbsence = partialAbsences.some(absence => {
        return (minutes < absence.end && slotEndMinutes > absence.start);
      });

      if (!isDuringLunch && !isDuringAbsence) {
        // Utiliser la durée totale (service + repos) pour vérifier les chevauchements
        const totalSlotEndMinutes = minutes + totalDuration;

        // Pour les rendez-vous à distance, nous n'avons pas besoin de vérifier les chevauchements avec les rendez-vous présentiels
        // et vice versa, sauf si le praticien ne peut pas faire les deux types de rendez-vous simultanément
        const relevantBookedSlots = bookedSlots.filter(slot => {
          // Si le service actuel est à distance et que le créneau réservé est en présentiel,
          // ou si le service actuel est en présentiel et que le créneau réservé est à distance,
          // et que le praticien peut faire les deux types en même temps, on ne compte pas ce créneau comme un chevauchement
          if ((isRemote && !slot.isRemote) || (!isRemote && slot.isRemote)) {
            // Ici, vous pourriez ajouter une vérification pour savoir si le praticien peut gérer les deux types simultanément
            // Par exemple, en ajoutant un flag dans les paramètres de la fonction ou dans les disponibilités du praticien
            // Pour l'instant, on suppose qu'il ne peut pas (donc tous les créneaux sont considérés comme occupant le praticien)
            return true; // Considérer tous les rendez-vous comme des chevauchements potentiels
          }
          return true;
        });

        // Vérifier si le créneau avec son temps de repos chevauche un rendez-vous existant
        const isOverlapping = relevantBookedSlots.some(blockedSlot => {
          // Un chevauchement existe si:
          // - Le début du nouveau créneau est avant la fin du créneau bloqué ET
          // - La fin du nouveau créneau (avec repos) est après le début du créneau bloqué
          return (minutes < blockedSlot.end && totalSlotEndMinutes > blockedSlot.start);
        });

        if (!isOverlapping) {
          slots.push({
            time: minutesToTimeString(minutes),
            minutes: minutes,
            endTime: minutesToTimeString(minutes + duration), // Heure de fin du service (sans repos)
            endTimeWithRest: minutesToTimeString(minutes + totalDuration), // Heure de fin avec repos
            isRemote: isRemote // Indiquer si c'est un créneau à distance
          });
        } else {
          console.log(`Créneau ${minutesToTimeString(minutes)} chevauchant un rendez-vous existant`);
        }
      } else {
        if (isDuringLunch) {
          console.log(`Créneau ${minutesToTimeString(minutes)} durant la pause déjeuner`);
        }
        if (isDuringAbsence) {
          console.log(`Créneau ${minutesToTimeString(minutes)} durant une absence partielle`);
        }
      }
    }

    return slots;
  };

  // Convertir une heure au format HH:MM en minutes depuis minuit
  const convertTimeToMinutes = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convertir des minutes depuis minuit en heure au format HH:MM
  const minutesToTimeString = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Gérer la sélection d'un créneau horaire
  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot.time);

    // Notifier le composant parent
    if (onTimeSelect) {
      onTimeSelect(timeSlot.time);
    }

    // Mettre à jour la date formatée pour l'affichage
    const formattedDate = selectedDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    updateBookingData({
      formattedDate,
      // Conserver le choix de type de consultation fait à l'étape 3
      isRemote: isRemoteAppointment || isRemoteOnlyDay || bookingData.remoteOnly
    });

    // Passer à l'étape suivante automatiquement
    setCurrentStep(5);
  };

  // Gérer le retour à l'étape précédente
  const handleBack = () => {
    setCurrentStep(3);
  };

  return (
    <div className="time-slots-container">
      <h2>Choisissez un horaire</h2>
      <p className="date-selected">
        {selectedDate.toLocaleDateString('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })}
      </p>

      {/* Badge simplifié pour les jours ou services à distance */}
      {(isRemoteOnlyDay || bookingData.remoteOnly || isRemoteAppointment) && (
        <div className="remote-only-badge">
          {isRemoteOnlyDay
            ? "Consultations à distance uniquement ce jour"
            : bookingData.remoteOnly
              ? "Ce service est dispensé uniquement à distance"
              : "Consultation à distance"}
        </div>
      )}

      {loading && (
        <div className="time-slots-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des horaires...</p>
        </div>
      )}

      {error && (
        <div className="time-slots-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && timeSlots.length === 0 && (
        <div className="no-time-slots">
          <p>Aucun horaire disponible pour cette date.</p>
        </div>
      )}

      {!loading && !error && timeSlots.length > 0 && (
        <div className="time-slots-grid">
          {timeSlots.map((slot, index) => (
            <div
              key={index}
              className={`time-slot ${selectedTimeSlot === slot.time ? 'selected' : ''} ${slot.isRemote ? 'remote-slot' : ''}`}
              onClick={() => handleTimeSlotSelect(slot)}
              title={`${slot.time} - ${slot.endTime} ${slot.isRemote ? '(À distance)' : ''}`}
            >
              {slot.time}
              {slot.isRemote && <span className="remote-indicator"></span>}
            </div>
          ))}
        </div>
      )}

      <div className="step-navigation">
        <button className="btn btn-back" onClick={handleBack}>
          Retour
        </button>
      </div>
    </div>
  );
};

export default TimeSlots;